import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import React, { FC, useMemo } from 'react';
import { isNil } from 'lodash-es';
import { useRequest } from 'ahooks';
import { apiV2MicrogridOverviewBsaOperatingCurveNeoPost } from '@maxtropy/intelligent-dispath-apis-v2';
import dayjs from 'dayjs';
import {
  ammeterColorList,
  bsaColor,
  dash2LegendIcon,
  getEndLabel,
  inPowerColor,
  inPowerWithoutBsaColor,
  pcsColorList,
  solidLegendIcon,
  upperTransformerMeterPowerChartsColorList,
} from '../../const';

export const Chart_Grid_Right = 130; // 右边偏移量

export interface IPowerMonitor {
  bsaId?: number;
}

const PowerMonitor: FC<IPowerMonitor> = props => {
  const { bsaId } = props;
  const theme = useTheme();

  // 充放电功率
  const { data: powerData, loading } = useRequest(
    async () => {
      return await apiV2MicrogridOverviewBsaOperatingCurveNeoPost({
        bsaId: bsaId,
        bsaType: 1,
        time: dayjs().startOf('day').unix().toString(),
        timeResolution: '15M',
      });
    },
    {
      ready: !!bsaId,
      refreshDeps: [bsaId],
    }
  );

  const echartsOption = useMemo(() => {
    const option = {
      backgroundColor: 'transparent',
      grid: {
        left: powerData ? 10 : 24,
        right: powerData ? 10 : 24,
        top: 55,
        bottom: 10,
        containLabel: true,
      },
      xAxis: {
        type: 'time',
      },
      yAxis: {
        name: '功率(kW)',
        type: 'value',
        scale: true,
        boundaryGap: ['5%', '5%'],
      },
      tooltip: {
        formatter(items: any[]) {
          const { axisValueLabel } = items[0];
          let str: string = axisValueLabel;

          items.forEach(item => {
            const { seriesName, data, marker } = item;
            const value = !isNil(data[1]) ? `${data[1].toFixed(4)}` : '--';
            str += `<div style="display:flex;margin-top:4px;justify-content:space-between;gap:30px;"><span>${marker}${seriesName}</span> <span>${value}kW</span></div>`;
          });

          return str;
        },
      },
      legend: {
        show: true,
        type: 'scroll',
        right: 10,
        // textStyle: {
        //   fontSize: '13px',
        // },
        itemWidth: 16,
        itemHeight: 4,
        data: [],
      },
      // dataZoom: [{ start: 0, end: 100 }],
      series: [],
    };

    let legendData = option.legend.data as any[];
    let series = option.series as any[];

    legendData.push({
      name: '储能功率',
      icon: solidLegendIcon,
      itemStyle: {
        color: bsaColor,
      },
    });
    series.push({
      name: '储能功率',
      type: 'line',
      showSymbol: false,
      itemStyle: {
        color: bsaColor,
      },
      data: powerData?.bsaPower?.map(item => [item.ts, item.value]),
      // ...getEndLabel(powerData?.bsaPower?.map(item => [item.ts, item.value]) as any[]),
    });

    // 生产负载(不含储能的进线功率)
    legendData.push({
      name: '不含储能的进线功率',
      icon: solidLegendIcon,
      itemStyle: {
        color: inPowerWithoutBsaColor,
      },
    });
    series.push({
      name: '不含储能的进线功率',
      type: 'line',
      showSymbol: false,
      itemStyle: {
        color: inPowerWithoutBsaColor,
      },
      data: powerData?.incomingWzBsaPower?.map(item => [item.ts, item.value]),
      // ...getEndLabel(powerData?.incomingWzBsaPower?.map(item => [item.ts, item.value]) as any[]),
    });

    // 进线功率
    legendData.push({
      name: '进线功率',
      icon: solidLegendIcon,
      itemStyle: {
        color: inPowerColor,
      },
    });
    series.push({
      name: '进线功率',
      type: 'line',
      showSymbol: false,
      itemStyle: {
        color: inPowerColor,
      },
      data: powerData?.incomingPower?.map(item => [item.ts, item.value]),
      // ...getEndLabel(powerData?.incomingPower?.map(item => [item.ts, item.value]) as any[]),
    });

    // 上端变压器功率
    let upTransformerList = powerData?.upperTransformerMeterPower;
    upTransformerList?.forEach((item, index) => {
      legendData.push({
        name: item.name,
        icon: solidLegendIcon,
        itemStyle: {
          color: ammeterColorList[index % ammeterColorList.length],
        },
      });
      series.push({
        name: item.name,
        type: 'line',
        showSymbol: false,
        itemStyle: {
          color: ammeterColorList[index % ammeterColorList.length],
        },
        data: item.list?.map(dataItem => [dataItem.ts, dataItem.value]) ?? [],
        // ...getEndLabel(item.list?.map(dataItem => [dataItem.ts, dataItem.value]) as any[]),
      });
    });

    // 各pcs功率
    powerData?.pcsCurve?.forEach((item, index) => {
      legendData.push({
        name: item.name,
        icon: dash2LegendIcon,
        itemStyle: {
          color: pcsColorList[index % pcsColorList.length],
        },
      });
      series.push({
        name: item.name,
        type: 'line',
        showSymbol: false,
        itemStyle: {
          color: pcsColorList[index % pcsColorList.length],
        },
        lineStyle: {
          type: [6, 6],
        },
        smooth: true,
        data: item.list?.map(dataItem => [dataItem.ts, dataItem.value]) ?? [],
        // ...getEndLabel(item.list?.map(dataItem => [dataItem.ts, dataItem.value]) as any[]),
      });
    });

    // 不含储能的上端变压器功率
    const upperTransformerMeterPower = powerData?.upperTransformerMeterPowerV2;
    upperTransformerMeterPower?.forEach((item, index) => {
      legendData.push({
        name: (item.name ?? '--') + '（不含储能）',
        icon: solidLegendIcon,
        itemStyle: {
          color: upperTransformerMeterPowerChartsColorList[index % upperTransformerMeterPowerChartsColorList.length],
        },
      });
      series.push({
        name: (item.name ?? '--') + '（不含储能）',
        type: 'line',
        showSymbol: false,
        itemStyle: {
          color: upperTransformerMeterPowerChartsColorList[index % upperTransformerMeterPowerChartsColorList.length],
        },
        data: item.list?.map(dataItem => [dataItem.ts, dataItem.value]) ?? [],
        // ...getEndLabel(item.list?.map(dataItem => [dataItem.ts, dataItem.value]) as any[]),
      });
    });

    return option;
  }, [powerData]);

  const notEmpty = useMemo(() => {
    return echartsOption.series?.some((item: any) => {
      return item.data?.length;
    });
  }, [echartsOption]);

  return (
    <>
      <EchartsWithTheme
        empty={{
          emptyNode: <Empty style={{ marginTop: 30 }} />,
          isShowEmpty: !notEmpty,
        }}
        loading={loading}
        option={echartsOption}
        theme={theme}
        style={{ height: '100%' }}
      />
    </>
  );
};

export default PowerMonitor;
